import { IPriceHistory, IUnitWithPrice } from 'my-poppies-b2b-types';
import { useContext, useState } from 'react';

import { colors } from '@/colors';
import { Container } from '@/components/Container';
import { InfoIcon } from '@/components/InfoIcon';
import { Since } from '@/components/Since';
import { DataContext } from '@/contexts/DataContext';
import { LanguageContext } from '@/contexts/LanguageContext';
import { usePrice } from '@/hooks/usePrice';
import useTranslation from '@/hooks/useTranslation';
import { prettifyTimestamp } from '@/utils/getNow';
import { toastInfo } from '@/utils/toastInfo';

const HISTORY_MARGIN = 8;
const BORDER_WIDTH = 3;

interface IPriceHistorySectionProps {
  title: string;
  data: IPriceHistory;
  unit: IUnitWithPrice;
}

export const PriceHistorySection = ({ title, data, unit }: IPriceHistorySectionProps) => {
  const { currentLanguageWithFallback } = useContext(LanguageContext);
  const price = usePrice(currentLanguageWithFallback);
  const t = useTranslation();
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const { setChangePriceModalData } = useContext(DataContext);
  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
      <Container sx={{ fontWeight: 'bold', padding: HISTORY_MARGIN }}>{title}</Container>
      <Container sx={{ display: 'flex', flexDirection: 'column', padding: '0 8px' }}>
        {data.map((pp, index) => {
          if ((!showHistory || data.length <= 1) && index > 0) {
            return null;
          }
          return (
            <Container
              key={`priceEntry.pallets.${pp.createdAt}`}
              sx={{
                backgroundColor: colors.grayMedium,
                display: 'flex',
                flexDirection: 'column',
                fontWeight: index === 0 ? 'bold' : 'unset',
              }}
            >
              <Container
                sx={{
                  opacity: index === 0 ? 1 : 0.65,
                  border: index === 0 ? 'unset' : `${BORDER_WIDTH}px solid white`,
                  borderBottomWidth: index === data.length - 1 ? undefined : 0,
                  backgroundColor: index % 2 !== 0 ? colors.gray : colors.grayMedium,
                  marginLeft: index === 0 ? '0' : `${HISTORY_MARGIN}px`,
                  marginBottom: index !== data.length - 1 ? '0' : `${HISTORY_MARGIN}px`,
                  width: index === 0 ? '100%' : `calc(100% - ${2 * HISTORY_MARGIN}px)`,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Container sx={{ flex: 0, padding: index === 0 ? `4px ${4 + HISTORY_MARGIN}px` : 4 }}>
                  {price(pp.price)}
                </Container>
                <Container
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    fontSize: 12,
                    textAlign: 'right',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingRight: index === 0 ? 4 + HISTORY_MARGIN + BORDER_WIDTH : 4,
                  }}
                >
                  <Since
                    prefix={t('price.setPrefix')}
                    suffix={t('price.setSuffix', undefined, undefined, { createdBy: pp.createdBy })}
                    timestamp={pp.createdAt}
                    locale={currentLanguageWithFallback}
                  />
                  <Container
                    sx={{ flewGrow: 0, margin: 'unset', width: 'unset', marginLeft: 8 }}
                    onClick={() => {
                      toastInfo(
                        <div>
                          <span style={{ fontWeight: 'bold' }}>{t('momentInTime')}:</span>
                          <br />
                          {prettifyTimestamp(pp.createdAt, currentLanguageWithFallback)}
                        </div>,
                        {
                          id: 'unique-id-for-timestamp-display',
                        }
                      );
                    }}
                  >
                    <InfoIcon cursorPointer />
                  </Container>
                  {/*<Container sx={{}}>🕐 {prettifyTimestamp(pp.createdAt, currentLanguageWithFallback)}</Container>
                  <Container sx={{}}>👤 {pp.createdBy}</Container>*/}
                </Container>
              </Container>
              {index === 0 && (
                <Container
                  sx={{
                    padding: `0 ${HISTORY_MARGIN + BORDER_WIDTH}px`,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Container
                    onClick={() => {
                      setChangePriceModalData({ unit });
                    }}
                    sx={{
                      marginBottom: 4,
                      textAlign: 'left',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: 12,
                      flex: 1,
                      textWrap: 'nowrap',
                    }}
                  >
                    {t('price.changePrice')}
                  </Container>
                  {data.length > 1 && (
                    <Container
                      onClick={() => {
                        setShowHistory((prevState) => !prevState);
                      }}
                      sx={{
                        marginBottom: 4,
                        textAlign: 'right',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: 12,
                        flex: 1,
                        textWrap: 'nowrap',
                      }}
                    >
                      {showHistory ? `- ${t('price.hideHistory')}` : `+ ${t('price.showHistory')}`}
                    </Container>
                  )}
                </Container>
              )}
            </Container>
          );
        })}
      </Container>
      {data.length === 0 && (
        <Container
          onClick={() => {
            setChangePriceModalData({ unit });
          }}
          sx={{
            marginBottom: 4,
            textAlign: 'center',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: 12,
            flex: 1,
            textWrap: 'nowrap',
            fontWeight: 'bold',
          }}
        >
          {t('price.changePriceFirst')}
        </Container>
      )}
    </Container>
  );
};
